// Validation utility functions

export const validators = {
  // Name validation - only letters and spaces
  name: (value) => {
    if (!value || value.trim() === '') {
      return 'This field is required';
    }
    if (!/^[a-zA-Z\s]+$/.test(value)) {
      return 'Only letters and spaces are allowed';
    }
    if (value.trim().length < 2) {
      return 'Name must be at least 2 characters';
    }
    return '';
  },

  // Mobile number validation - Indian (10 digits) or International (+countrycode)
  mobile: (value) => {
    if (!value || value.trim() === '') {
      return 'Mobile number is required';
    }
    // Strip spaces for validation (international numbers may have spaces)
    const v = value.trim().replace(/\s/g, '');
    // Indian: exactly 10 digits starting with 6-9
    const isIndian = /^[6-9]\d{9}$/.test(v);
    // International: starts with + followed by 7-15 digits (E.164 format, spaces removed)
    const isInternational = /^\+[1-9]\d{6,14}$/.test(v);
    if (!isIndian && !isInternational) {
      return 'Enter 10-digit Indian number or international number with country code (e.g. +44 20 7946 0958)';
    }
    return '';
  },

  // Email validation
  email: (value) => {
    if (!value || value.trim() === '') {
      return 'Email is required';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return 'Please enter a valid email address';
    }
    return '';
  },

  // Required field validation
  required: (value) => {
    if (!value || value.trim() === '') {
      return 'This field is required';
    }
    return '';
  },

  // Date of birth validation
  dob: (value) => {
    if (!value) {
      return 'Date of birth is required';
    }
    const date = new Date(value);
    const today = new Date();
    const age = today.getFullYear() - date.getFullYear();
    
    if (age < 15) {
      return 'Age must be at least 15 years';
    }
    if (age > 100) {
      return 'Please enter a valid date of birth';
    }
    return '';
  },

  // Blood group validation
  bloodGroup: (value) => {
    if (!value) return ''; // Optional field
    const validGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
    if (!validGroups.includes(value.toUpperCase())) {
      return 'Please enter a valid blood group (A+, A-, B+, B-, AB+, AB-, O+, O-)';
    }
    return '';
  },

  // Aadhar card validation - 12 digits
  aadhar: (value) => {
    if (!value) return ''; // Optional field
    if (!/^\d{12}$/.test(value)) {
      return 'Aadhar number must be exactly 12 digits';
    }
    return '';
  },

  // Guardian ID number — validates based on type passed as second arg
  // Usage: validators.guardianIdNumber(value, idType)
  guardianIdNumber: (value, idType) => {
    if (!value || value.trim() === '') return ''; // optional
    switch (idType) {
      case 'Aadhar':
        return /^\d{12}$/.test(value) ? '' : 'Aadhar must be exactly 12 digits';
      case 'DrivingLicense':
        return /^[A-Z]{2}\d{2}\s?\d{4}\d{7}$/.test(value.replace(/\s/g,''))
          ? '' : 'Driving License format: XX00XXXXXXXX (e.g. MH1220150012345)';
      case 'Passport':
        return /^[A-Z]\d{7}$/i.test(value) ? '' : 'Passport: 1 letter + 7 digits (e.g. A1234567)';
      default:
        return '';
    }
  },

  // Pincode/Zipcode validation - 6 digits
  pincode: (value) => {
    if (!value || value.trim() === '') {
      return 'Pincode is required';
    }
    if (!/^\d{6}$/.test(value)) {
      return 'Pincode must be exactly 6 digits';
    }
    return '';
  },

  // Optional email validation
  optionalEmail: (value) => {
    if (!value || value.trim() === '') {
      return ''; // Optional
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return 'Please enter a valid email address';
    }
    return '';
  },

  // Optional mobile validation - Indian or International
  optionalMobile: (value) => {
    if (!value || value.trim() === '') {
      return ''; // Optional
    }
    // Strip spaces for validation
    const v = value.trim().replace(/\s/g, '');
    const isIndian = /^[6-9]\d{9}$/.test(v);
    const isInternational = /^\+[1-9]\d{6,14}$/.test(v);
    if (!isIndian && !isInternational) {
      return 'Enter 10-digit Indian number or international number with country code';
    }
    return '';
  },

  // Gender validation
  gender: (value) => {
    if (!value || value.trim() === '') {
      return 'Gender is required';
    }
    return '';
  },

  // Address validation
  address: (value) => {
    if (!value || value.trim() === '') {
      return 'Address is required';
    }
    if (value.trim().length < 10) {
      return 'Address must be at least 10 characters';
    }
    return '';
  },

  // City validation
  city: (value) => {
    if (!value || value.trim() === '') {
      return 'City is required';
    }
    if (!/^[a-zA-Z\s]+$/.test(value)) {
      return 'Only letters and spaces are allowed';
    }
    return '';
  },

  // Capitalised name validator — each word must start with a capital letter
  // Used for: Full Name, Father's Name, Mother's Name, Religion,
  //           City, State, Country  (anywhere first-letter-capital is required)
  capitalFirst: (value) => {
    if (!value || value.trim() === '') {
      return 'This field is required';
    }
    if (!/^[a-zA-Z\s]+$/.test(value)) {
      return 'Only letters and spaces are allowed';
    }
    if (value.trim().length < 2) {
      return 'Must be at least 2 characters';
    }
    const words = value.trim().split(/\s+/);
    for (const word of words) {
      if (word.length > 0 && word[0] !== word[0].toUpperCase()) {
        return 'Each word must start with a capital letter (e.g. John Smith)';
      }
    }
    return '';
  },

  // Optional version — only validates if value is provided
  optionalCapitalFirst: (value) => {
    if (!value || value.trim() === '') return '';   // optional — blank is fine
    if (!/^[a-zA-Z\s]+$/.test(value)) {
      return 'Only letters and spaces are allowed';
    }
    const words = value.trim().split(/\s+/);
    for (const word of words) {
      if (word.length > 0 && word[0] !== word[0].toUpperCase()) {
        return 'Each word must start with a capital letter';
      }
    }
    return '';
  },

  // Address line: first letter must be capital, min 10 chars
  capitalFirstAddress: (value) => {
    if (!value || value.trim() === '') {
      return 'Address is required';
    }
    if (value.trim().length < 10) {
      return 'Address must be at least 10 characters';
    }
    if (value[0] !== value[0].toUpperCase() || !/[A-Z]/.test(value[0])) {
      return 'Address must start with a capital letter';
    }
    return '';
  },
};

// Validate all fields
export const validateForm = (formData, validationRules) => {
  const errors = {};
  let isValid = true;

  Object.keys(validationRules).forEach(field => {
    const validator = validationRules[field];
    const error = validator(formData[field]);
    if (error) {
      errors[field] = error;
      isValid = false;
    }
  });

  return { isValid, errors };
};